/* eslint-disable no-unused-vars */
import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  SafeAreaView,
  ScrollView,
  Dimensions,
  Image,
  Alert,
  ActivityIndicator,
  Animated,
} from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { useAuth } from '../../context/AuthContext';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { LinearGradient } from 'expo-linear-gradient';

const { width, height } = Dimensions.get('window');

export default function WelcomeLearningScreen({ navigation, route }) {
  const { user, login } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [userName, setUserName] = useState('');
  const [userEducation, setUserEducation] = useState('');
  const [fadeAnim] = useState(new Animated.Value(0));
  const [slideAnim] = useState(new Animated.Value(50));
  const [scaleAnim] = useState(new Animated.Value(0.8));

  // Enhanced gradient color schemes
  const gradients = {
    primary: ['#667eea', '#764ba2'],
    secondary: ['#f093fb', '#f5576c'],
    success: ['#4facfe', '#00f2fe'],
    warning: ['#43e97b', '#38f9d7'],
    danger: ['#fa709a', '#fee140'],
    info: ['#a8edea', '#fed6e3'],
    purple: ['#d299c2', '#fef9d7'],
    orange: ['#fdbb2d', '#22c1c3'],
    blue: ['#2196f3', '#21cbf3'],
    green: ['#56ab2f', '#a8e6cf'],
    pink: ['#ff9a9e', '#fecfef'],
    teal: ['#667eea', '#764ba2'],
  };

  useEffect(() => {
    const getUserData = async () => {
      try {
        // First try to get from route params (passed from signup)
        const routeUser = route?.params?.user;
        
        // Then try from AuthContext
        const contextUser = user;
        
        // Finally try from AsyncStorage
        const storedUserData = await AsyncStorage.getItem('user_data');
        const storedUser = storedUserData ? JSON.parse(storedUserData) : null;
        
        // Use the most complete user data available
        const currentUser = routeUser || contextUser || storedUser;
        
        console.log('Welcome screen user data:', currentUser);
        
        if (currentUser) {
          // Extract name from various possible fields
          const fullName = currentUser.full_name || 
                          currentUser.fullName || 
                          currentUser.name || 
                          currentUser.first_name || 
                          'Engineer';
          
          const education = currentUser.education_level || 
                           currentUser.educationLevel || 
                           'Engineering Student';
          
          setUserName(fullName);
          setUserEducation(education);
        } else {
          setUserName('Engineer');
          setUserEducation('Engineering Student');
        }
      } catch (error) {
        console.error('Error getting user data:', error);
        setUserName('Engineer');
        setUserEducation('Engineering Student');
      }
    };
    
    getUserData();
    startAnimations();
  }, [user, route]);

  const startAnimations = () => {
    Animated.parallel([
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 1000,
        useNativeDriver: true,
      }),
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: 800,
        useNativeDriver: true,
      }),
      Animated.timing(scaleAnim, {
        toValue: 1,
        duration: 1200,
        useNativeDriver: true,
      }),
    ]).start();
  };

  const onboardingSteps = [
    {
      title: `Welcome to JOBENG Learning, ${userName}!`,
      subtitle: '🚀 Your engineering career journey starts here',
      description: 'Access industry-standard courses designed by leading UK engineering firms and universities. Build skills that employers actually need.',
      icon: 'school',
      gradient: gradients.primary,
      features: [
        '🏗️ Civil Engineering Fundamentals',
        '🔧 Structural Design & Analysis',
        '📊 Project Management (APM Certified)',
        '💻 AutoCAD & Revit Training'
      ]
    },
    {
      title: '🎓 Industry-Recognized Courses',
      subtitle: '👨‍🏫 Learn from practicing engineers',
      description: 'Our courses are created by chartered engineers from Arup, Balfour Beatty, and other top UK firms. Get real-world knowledge, not just theory.',
      icon: 'engineering',
      gradient: gradients.orange,
      features: [
        '⚡ Chartered Engineer (CEng) Prep',
        '🛡️ Health & Safety (NEBOSH)',
        '📋 Building Regulations & Standards',
        '🌱 Sustainability & Green Building'
      ]
    },
    {
      title: '💼 Virtual Work Experience',
      subtitle: '🌐 Gain practical experience remotely',
      description: 'Complete real engineering projects with mentorship from industry professionals. Build a portfolio that demonstrates your capabilities.',
      icon: 'laptop',
      gradient: gradients.success,
      features: [
        '🌉 Bridge Design Projects',
        '🏢 Building Information Modeling',
        '📐 Site Survey & Analysis',
        '💰 Cost Estimation & Planning'
      ]
    },
    {
      title: '📈 Career Advancement',
      subtitle: '🤝 Connect with UK engineering employers',
      description: 'Access exclusive job opportunities with our partner companies. Get career guidance from experienced engineers in your field.',
      icon: 'trending-up',
      gradient: gradients.warning,
      features: [
        '🎯 Graduate Engineer Positions',
        '🔧 Apprenticeship Opportunities',
        '👥 Professional Mentorship',
        '🌐 Industry Networking Events'
      ]
    },
  ];

  const handleNext = () => {
    if (currentStep < onboardingSteps.length - 1) {
      setCurrentStep(currentStep + 1);
      // Restart animations for new step
      fadeAnim.setValue(0);
      slideAnim.setValue(50);
      scaleAnim.setValue(0.8);
      startAnimations();
    } else {
      handleGetStarted();
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      // Restart animations for previous step
      fadeAnim.setValue(0);
      slideAnim.setValue(50);
      scaleAnim.setValue(0.8);
      startAnimations();
    }
  };

  const handleSkip = () => {
    handleGetStarted();
  };

  const handleGetStarted = async () => {
    setIsLoading(true);
    
    try {
      console.log('Completing learning onboarding for user:', userName);
      
      // Navigate directly to LearningDashboard
      navigation.navigate('LearningDashboard');
      
    } catch (error) {
      console.error('Learning onboarding error:', error);
      Alert.alert('Error', 'Something went wrong. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const currentStepData = onboardingSteps[currentStep];
  const isLastStep = currentStep === onboardingSteps.length - 1;

  return (
    <SafeAreaView style={styles.safeArea}>
      {/* Enhanced Header with Gradient */}
      <LinearGradient
        colors={gradients.primary}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 1 }}
        style={styles.headerGradient}
      >
        <View style={styles.header}>
          <Animated.View 
            style={[
              styles.headerContent,
              {
                opacity: fadeAnim,
                transform: [{ translateY: slideAnim }],
              },
            ]}
          >
            <View style={styles.logoContainer}>
              <LinearGradient
                colors={gradients.orange}
                style={styles.logoGradient}
              >
                <MaterialCommunityIcons name="school" size={40} color="#ffffff" />
              </LinearGradient>
            </View>
            <Text style={styles.welcomeText}>
              ✨ Hello, {userName}! 🎓
            </Text>
            <Text style={styles.educationLevel}>
              📚 {userEducation}
            </Text>
            <View style={styles.onlineIndicator}>
              <View style={styles.onlineDot} />
              <Text style={styles.onlineText}>Ready to Learn</Text>
            </View>
          </Animated.View>
          <TouchableOpacity onPress={handleSkip} style={styles.skipButton}>
            <LinearGradient
              colors={gradients.danger}
              style={styles.skipGradient}
            >
              <Text style={styles.skipText}>Skip Tour</Text>
            </LinearGradient>
          </TouchableOpacity>
        </View>
      </LinearGradient>

      <ScrollView
        contentContainerStyle={styles.scrollContainer}
        showsVerticalScrollIndicator={false}
      >
        <View style={styles.container}>
          {/* Enhanced Progress Indicator */}
          <Animated.View 
            style={[
              styles.progressContainer,
              {
                opacity: fadeAnim,
                transform: [{ scale: scaleAnim }],
              },
            ]}
          >
            {onboardingSteps.map((step, index) => (
              <View key={index} style={styles.progressDotContainer}>
                {index <= currentStep ? (
                  <LinearGradient
                    colors={step.gradient}
                    style={styles.progressDotGradient}
                  >
                    <MaterialCommunityIcons 
                      name={index === currentStep ? "circle" : "check"} 
                      size={12} 
                      color="#ffffff" 
                    />
                  </LinearGradient>
                ) : (
                  <View style={styles.progressDotInactive} />
                )}
              </View>
            ))}
          </Animated.View>

          {/* Enhanced Main Content */}
          <Animated.View 
            style={[
              styles.contentContainer,
              {
                opacity: fadeAnim,
                transform: [{ translateY: slideAnim }],
              },
            ]}
          >
            {/* Enhanced Icon with Gradient */}
            <Animated.View 
              style={[
                styles.iconContainer,
                {
                  transform: [{ scale: scaleAnim }],
                },
              ]}
            >
              <LinearGradient
                colors={currentStepData.gradient}
                style={styles.iconGradient}
              >
                <MaterialCommunityIcons 
                  name={currentStepData.icon} 
                  size={64} 
                  color="#ffffff" 
                />
              </LinearGradient>
            </Animated.View>

            {/* Enhanced Text Content */}
            <View style={styles.textContainer}>
              <Text style={styles.stepTitle}>
                {currentStep === 0 ? `Welcome to JOBENG Learning, ${userName}!` : currentStepData.title}
              </Text>
              <Text style={styles.stepSubtitle}>{currentStepData.subtitle}</Text>
              <Text style={styles.stepDescription}>{currentStepData.description}</Text>
            </View>

            {/* Enhanced Features List */}
            <View style={styles.featuresContainer}>
              <LinearGradient
                colors={['#f8fafc', '#ffffff']}
                style={styles.featuresGradient}
              >
                <Text style={styles.featuresTitle}>🎯 Available Now:</Text>
                <View style={styles.featuresList}>
                  {currentStepData.features.map((feature, index) => (
                    <Animated.View 
                      key={index} 
                      style={[
                        styles.featureItem,
                        {
                          opacity: fadeAnim,
                          transform: [{ translateX: slideAnim }],
                        },
                      ]}
                    >
                      <LinearGradient
                        colors={currentStepData.gradient}
                        style={styles.featureIconGradient}
                      >
                        <MaterialCommunityIcons name="check" size={14} color="#ffffff" />
                      </LinearGradient>
                      <Text style={styles.featureText}>{feature}</Text>
                    </Animated.View>
                  ))}
                </View>
              </LinearGradient>
            </View>

            {/* Enhanced Learning Stats */}
            {currentStep === 0 && (
              <Animated.View 
                style={[
                  styles.statsContainer,
                  {
                    opacity: fadeAnim,
                    transform: [{ scale: scaleAnim }],
                  },
                ]}
              >
                <LinearGradient
                  colors={gradients.orange}
                  style={styles.statsGradient}
                >
                  <View style={styles.statItem}>
                    <Text style={styles.statNumber}>500+</Text>
                    <Text style={styles.statLabel}>📚 Courses</Text>
                  </View>
                  <View style={styles.statDivider} />
                  <View style={styles.statItem}>
                    <Text style={styles.statNumber}>50+</Text>
                    <Text style={styles.statLabel}>🏢 Partners</Text>
                  </View>
                  <View style={styles.statDivider} />
                  <View style={styles.statItem}>
                    <Text style={styles.statNumber}>95%</Text>
                    <Text style={styles.statLabel}>💼 Job Rate</Text>
                  </View>
                </LinearGradient>
              </Animated.View>
            )}

            {/* Enhanced Personal Message */}
            {currentStep === 0 && (
              <Animated.View 
                style={[
                  styles.personalMessageContainer,
                  {
                    opacity: fadeAnim,
                    transform: [{ translateY: slideAnim }],
                  },
                ]}
              >
                <LinearGradient
                  colors={gradients.success}
                  style={styles.personalMessageGradient}
                >
                  <MaterialCommunityIcons name="rocket-launch" size={24} color="#ffffff" />
                  <Text style={styles.personalMessage}>
                    Ready to advance your engineering career, {userName}? Let's get started! 🚀
                  </Text>
                </LinearGradient>
              </Animated.View>
            )}
          </Animated.View>

          {/* Enhanced Bottom Navigation */}
          <Animated.View 
            style={[
              styles.bottomContainer,
              {
                opacity: fadeAnim,
                transform: [{ translateY: slideAnim }],
              },
            ]}
          >
            <View style={styles.navigationButtons}>
              {currentStep > 0 && (
                <TouchableOpacity onPress={handlePrevious} style={styles.previousButton}>
                  <LinearGradient
                    colors={gradients.info}
                    style={styles.previousGradient}
                  >
                    <MaterialCommunityIcons name="chevron-left" size={20} color="#ffffff" />
                    <Text style={styles.previousText}>Previous</Text>
                  </LinearGradient>
                </TouchableOpacity>
              )}

              <TouchableOpacity
                onPress={handleNext}
                style={[
                  styles.nextButton,
                  currentStep === 0 && styles.fullWidthButton,
                ]}
                disabled={isLoading}
              >
                <LinearGradient
                  colors={currentStepData.gradient}
                  style={styles.nextGradient}
                >
                  {isLoading ? (
                    <ActivityIndicator size={20} color="#FFFFFF" />
                  ) : (
                    <>
                      <Text style={styles.nextText}>
                        {isLastStep ? '🎯 Enter Dashboard' : '➡️ Continue'}
                      </Text>
                      {!isLastStep && <MaterialCommunityIcons name="chevron-right" size={20} color="#ffffff" />}
                    </>
                  )}
                </LinearGradient>
              </TouchableOpacity>
            </View>

            {/* Enhanced Step Counter */}
            <View style={styles.stepCounterContainer}>
              <LinearGradient
                colors={gradients.purple}
                style={styles.stepCounterGradient}
              >
                <Text style={styles.stepCounter}>
                  📍 Step {currentStep + 1} of {onboardingSteps.length}
                </Text>
              </LinearGradient>
            </View>
          </Animated.View>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#f0f4f8',
  },
  headerGradient: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.25,
    shadowRadius: 12,
    elevation: 8,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: 24,
  },
  headerContent: {
    flex: 1,
    alignItems: 'center',
  },
  logoContainer: {
    marginBottom: 16,
  },
  logoGradient: {
    width: 80,
    height: 80,
    borderRadius: 40,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.3,
    shadowRadius: 8,
    elevation: 8,
  },
  welcomeText: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#ffffff',
    textAlign: 'center',
    marginBottom: 8,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  educationLevel: {
    fontSize: 16,
    color: '#ffffff',
    textAlign: 'center',
    fontWeight: '600',
    marginBottom: 12,
    opacity: 0.9,
  },
  onlineIndicator: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  onlineDot: {
    width: 8,
    height: 8,
    borderRadius: 4,
    backgroundColor: '#4ade80',
    marginRight: 8,
  },
  onlineText: {
    fontSize: 14,
    color: '#ffffff',
    opacity: 0.9,
    fontWeight: '500',
  },
  skipButton: {
    padding: 4,
  },
  skipGradient: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
  },
  skipText: {
    fontSize: 14,
    color: '#ffffff',
    fontWeight: '600',
  },
  scrollContainer: {
    flexGrow: 1,
  },
  container: {
    flex: 1,
    padding: 24,
  },
  progressContainer: {
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    paddingVertical: 24,
    marginBottom: 20,
  },
  progressDotContainer: {
    marginHorizontal: 8,
  },
  progressDotGradient: {
    width: 32,
    height: 32,
    borderRadius: 16,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.25,
    shadowRadius: 8,
    elevation: 6,
  },
  progressDotInactive: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#e5e7eb',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  contentContainer: {
    flex: 1,
    alignItems: 'center',
    backgroundColor: '#ffffff',
    borderRadius: 24,
    padding: 28,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
    marginBottom: 24,
  },
  iconContainer: {
    marginBottom: 28,
  },
  iconGradient: {
    width: 120,
    height: 120,
    borderRadius: 60,
    justifyContent: 'center',
    alignItems: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.25,
    shadowRadius: 12,
    elevation: 8,
  },
  textContainer: {
    alignItems: 'center',
    marginBottom: 28,
  },
  stepTitle: {
    fontSize: 26,
    fontWeight: 'bold',
    color: '#1f2937',
    textAlign: 'center',
    marginBottom: 12,
  },
  stepSubtitle: {
    fontSize: 18,
    color: '#667eea',
    textAlign: 'center',
    marginBottom: 16,
    fontWeight: '600',
  },
  stepDescription: {
    fontSize: 16,
    color: '#6b7280',
    textAlign: 'center',
    lineHeight: 26,
  },
  featuresContainer: {
    width: '100%',
    marginBottom: 24,
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  featuresGradient: {
    padding: 24,
  },
  featuresTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#1f2937',
    marginBottom: 20,
    textAlign: 'center',
  },
  featuresList: {
    gap: 16,
  },
  featureItem: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  featureIconGradient: {
    width: 24,
    height: 24,
    borderRadius: 12,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 4,
    elevation: 3,
  },
  featureText: {
    fontSize: 16,
    color: '#374151',
    fontWeight: '500',
    flex: 1,
  },
  statsContainer: {
    width: '100%',
    marginBottom: 20,
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.25,
    shadowRadius: 12,
    elevation: 8,
  },
  statsGradient: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    padding: 24,
  },
  statItem: {
    alignItems: 'center',
    flex: 1,
  },
  statDivider: {
    width: 1,
    height: 40,
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    marginHorizontal: 16,
  },
  statNumber: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#ffffff',
    marginBottom: 4,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  statLabel: {
    fontSize: 14,
    color: '#ffffff',
    fontWeight: '600',
    textAlign: 'center',
    opacity: 0.9,
  },
  personalMessageContainer: {
    width: '100%',
    borderRadius: 20,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.25,
    shadowRadius: 12,
    elevation: 8,
  },
  personalMessageGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 20,
  },
  personalMessage: {
    fontSize: 16,
    color: '#ffffff',
    fontWeight: '600',
    marginLeft: 12,
    flex: 1,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  bottomContainer: {
    backgroundColor: '#ffffff',
    borderRadius: 20,
    padding: 24,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  navigationButtons: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  previousButton: {
    borderRadius: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 4,
  },
  previousGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    paddingHorizontal: 20,
  },
  previousText: {
    fontSize: 16,
    color: '#ffffff',
    marginLeft: 8,
    fontWeight: '600',
  },
  nextButton: {
    borderRadius: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 8 },
    shadowOpacity: 0.25,
    shadowRadius: 12,
    elevation: 8,
    minWidth: 160,
  },
  fullWidthButton: {
    flex: 1,
    marginLeft: 0,
  },
  nextGradient: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 16,
    paddingHorizontal: 24,
  },
  nextText: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#ffffff',
    marginRight: 8,
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
  stepCounterContainer: {
    alignItems: 'center',
    borderRadius: 16,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.15,
    shadowRadius: 8,
    elevation: 4,
  },
  stepCounterGradient: {
    paddingHorizontal: 20,
    paddingVertical: 10,
  },
  stepCounter: {
    fontSize: 14,
    color: '#ffffff',
    fontWeight: '600',
    textShadowColor: 'rgba(0, 0, 0, 0.3)',
    textShadowOffset: { width: 1, height: 1 },
    textShadowRadius: 3,
  },
});
